// Offline use and installing as an app. The service worker (pwa/sw.ts) keeps every file of the version
// that is open; a newer version installs in the background and waits until the user reloads into it.
import { create } from 'zustand';

/** Chrome and Edge's `beforeinstallprompt` event (not in the DOM types). */
interface InstallPromptEvent extends Event {
  prompt(): Promise<unknown>;
}

interface PwaState {
  /** A newer version that is ready and waits for a reload. */
  update: ServiceWorker | null;
  /** The browser's offer to install the app, held until the user asks for it. */
  install: InstallPromptEvent | null;
}

export const usePwa = create<PwaState>(() => ({ update: null, install: null }));

const HOUR = 60 * 60 * 1000;

export function startPwa(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    usePwa.setState({ install: e as InstallPromptEvent });
  });
  window.addEventListener('appinstalled', () => usePwa.setState({ install: null }));
  // Only built sites have a service worker; in development every file changes all the time.
  if (import.meta.env.PROD && 'serviceWorker' in navigator) window.addEventListener('load', () => void register(), { once: true });
}

async function register(): Promise<void> {
  const container = navigator.serviceWorker;
  let registration: ServiceWorkerRegistration;
  try {
    registration = await container.register('/sw.js');
  } catch {
    return; // Some private windows and settings allow no service worker; the app still works online.
  }
  // A worker that finishes installing while another one runs this page is a newer version.
  const watch = (worker: ServiceWorker | null) => {
    if (!worker) return;
    const check = () => {
      if (worker.state === 'installed' && container.controller) usePwa.setState({ update: worker });
      else if (worker.state === 'redundant' && usePwa.getState().update === worker) usePwa.setState({ update: null });
    };
    check();
    worker.addEventListener('statechange', check);
  };
  watch(registration.waiting);
  watch(registration.installing);
  registration.addEventListener('updatefound', () => watch(registration.installing));
  // Tabs and installed apps can stay open for days.
  window.setInterval(() => void registration.update().catch(() => undefined), HOUR);
}

/** Switches to the waiting version and reloads into it. */
export function applyUpdate(): void {
  const worker = usePwa.getState().update;
  if (!worker) return;
  navigator.serviceWorker.addEventListener('controllerchange', () => window.location.reload(), { once: true });
  worker.postMessage('update');
}

/** Shows the browser's install dialog (it can be shown only once per offer). */
export async function installApp(): Promise<void> {
  const offer = usePwa.getState().install;
  if (!offer) return;
  usePwa.setState({ install: null });
  await offer.prompt();
}
